import { supabase } from '../lib/supabase';
import { parse } from 'date-fns';

import { nextDepartureService } from './nextDepartureService';

/**
 * Professional Client Dashboard Service
 * Fetches real-time data for authenticated professional clients from Supabase
 */

/**
 * Parse French date format to JavaScript Date (ROBUST VERSION)
 * Handles accents, case variations, and past years
 * @param {string} frenchDate - Date in format "15 Février 2026" or "15 février 2026"
 * @returns {Date|null} Parsed date or null if invalid
 */
function parseFrenchDate(frenchDate) {
  if (!frenchDate) return null;
  
  try {
    const now = new Date();
    
    // French month names mapping (with and without accents)
    const frenchMonths = {
      'janvier': 0, 'février': 1, 'fevrier': 1, 'mars': 2, 'avril': 3,
      'mai': 4, 'juin': 5, 'juillet': 6, 'août': 7, 'aout': 7,
      'septembre': 8, 'octobre': 9, 'novembre': 10, 'décembre': 11, 'decembre': 11
    };
    
    const departureDateStr = frenchDate?.toLowerCase()?.trim();
    
    // Parse French date format: "28 février 2026" or "28 fevrier 2025"
    const dateMatch = departureDateStr?.match(/(\d{1,2})\s+(janvier|février|fevrier|mars|avril|mai|juin|juillet|août|aout|septembre|octobre|novembre|décembre|decembre)\s+(\d{4})/i);
    
    if (dateMatch) {
      const day = parseInt(dateMatch?.[1], 10);
      const monthName = dateMatch?.[2]?.toLowerCase()?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, ''); // Remove accents
      let year = parseInt(dateMatch?.[3], 10);
      
      // Find month index (now handles both accented and non-accented versions)
      const monthIndex = frenchMonths?.[monthName];
      
      if (monthIndex !== undefined) {
        // Create departure date
        let departureDate = new Date(year, monthIndex, day, 23, 59, 59);
        
        // If departure date is in the past, use next year occurrence
        if (departureDate < now) {
          year = now?.getFullYear();
          departureDate = new Date(year, monthIndex, day, 23, 59, 59);
          
          // If still in the past (e.g., today is Feb 28 and departure is Feb 28), use next year
          if (departureDate < now) {
            departureDate = new Date(year + 1, monthIndex, day, 23, 59, 59);
          }
        }
        
        // Check if the parsed date is valid
        if (isNaN(departureDate?.getTime())) {
          console.warn('Invalid French date format:', frenchDate);
          return null;
        }
        
        return departureDate;
      } else {
        console.error('Could not parse month from:', monthName);
        return null;
      }
    } else {
      console.error('Could not parse departure date:', departureDateStr);
      return null;
    }
  } catch (error) {
    console.error('Error parsing French date:', frenchDate, error);
    return null;
  }
}

export const professionalClientDashboardService = {
  /**
   * Get comprehensive dashboard data for authenticated professional client
   * @param {string} professionalClientId - Optional professional client ID (if not provided, fetches from auth)
   * @returns {Promise<Object>} Complete dashboard data with real-time KPIs
   */
  async getDashboardData(professionalClientId = null) {
    try {
      let professionalClient;

      if (professionalClientId) {
        // Fetch by provided ID
        const { data: client, error: clientError } = await supabase
          ?.from('professional_clients')
          ?.select('*')
          ?.eq('id', professionalClientId)
          ?.maybeSingle();

        if (clientError) throw clientError;
        if (!client) throw new Error('Client professionnel introuvable');
        
        professionalClient = client;
      } else {
        // Fetch from authenticated user
        const { data: { user }, error: userError } = await supabase?.auth?.getUser();

        if (userError) throw userError;
        if (!user) throw new Error('Not authenticated');

        // Get professional client profile
        const { data: client, error: clientError } = await supabase
          ?.from('professional_clients')
          ?.select('*')
          ?.eq('auth_user_id', user?.id)
          ?.maybeSingle();

        if (clientError) throw clientError;
        if (!client) {
          // Check if user has professional account type in metadata
          const accountType = user?.user_metadata?.account_type;
          
          if (accountType !== 'professional') {
            throw new Error(
              'Ce compte n\'est pas un compte professionnel. Veuillez vous inscrire en tant que client professionnel pour accéder à ce tableau de bord.'
            );
          }
          
          // Professional account type exists but no profile record
          throw new Error(
            'Votre profil professionnel est en cours de création. Veuillez patienter quelques instants et actualiser la page. Si le problème persiste, contactez l\'administrateur.'
          );
        }

        professionalClient = client;
      }

      // Check if account is active
      if (professionalClient?.is_active === false) {
        throw new Error(
          'Votre compte professionnel est désactivé. Veuillez contacter l\'administrateur pour plus d\'informations.'
        );
      }

      // First, get the client record associated with this professional client's email
      const { data: clientRecord, error: clientRecordError } = await supabase
        ?.from('clients')?.select('id')?.eq('email', professionalClient?.email)
        ?.maybeSingle();

      if (clientRecordError) throw clientRecordError;

      // Fetch all barrel sales for this client
      const { data: barrelSales, error: salesError } = await supabase
        ?.from('barrel_sales')?.select('*')?.eq('customer_email', professionalClient?.email)?.order('created_at', { ascending: false });

      if (salesError) throw salesError;

      // Fetch all barrel deposits for this client
      const { data: barrelDeposits, error: depositsError } = await supabase
        ?.from('barrel_deposits')?.select(`*,employees:assigned_driver_id (id,name,phone)`)?.eq('client_name', professionalClient?.contact_name)?.order('created_at', { ascending: false });

      if (depositsError) throw depositsError;

      // Fetch all shipments for this client (using client_id if client record exists)
      let shipments = [];
      let shipmentsError = null;

      if (clientRecord?.id) {
        const { data, error } = await supabase
          ?.from('shipments')?.select(`*,container:container_id (id,container_code,status,departure_date,eta_date,vessel_name,shipping_line),shipment_items (id,quantity,price_unit,price_total,catalog_item:catalog_item_id (name,category)),shipment_events (id,status,location,description,event_date),shipment_files (id,file_type,file_url,file_name,bucket_name)`)?.eq('client_id', clientRecord?.id)?.order('created_at', { ascending: false });

        shipments = data || [];
        shipmentsError = error;
      }

      // Calculate real-time KPIs
      const now = new Date();
      const startOfYear = new Date(now.getFullYear(), 0, 1);

      // Expeditions en cours (shipments in transit)
      const expeditionsEnCours = shipments?.filter(s => 
        ['in_transit', 'customs', 'out_for_delivery']?.includes(s?.current_status)
      )?.length || 0;

      // Total expédié cette année (all shipments + barrel deposits this year)
      const shipmentsThisYear = shipments?.filter(s => 
        new Date(s?.created_at) >= startOfYear
      )?.length || 0;
      
      const barrelDepositsThisYear = barrelDeposits?.filter(d => 
        new Date(d?.created_at) >= startOfYear
      )?.length || 0;
      
      const totalExpedieCetteAnnee = shipmentsThisYear + barrelDepositsThisYear;

      // Prochaine facture (sum of unpaid shipments and barrel sales)
      const unpaidShipments = shipments?.filter(s => 
        s?.payment_status === 'non_paye' || s?.payment_status === 'partiel'
      );
      
      const unpaidBarrelSales = barrelSales?.filter(s => 
        s?.payment_status === 'pending' || s?.payment_status === 'partial'
      );

      const unpaidShipmentsTotal = unpaidShipments?.reduce((sum, s) => {
        const itemsTotal = s?.shipment_items?.reduce((itemSum, item) => 
          itemSum + (parseFloat(item?.price_total) || 0), 0
        ) || 0;
        return sum + itemsTotal;
      }, 0) || 0;

      const unpaidBarrelSalesTotal = unpaidBarrelSales?.reduce((sum, s) => 
        sum + (parseFloat(s?.total_price) || 0), 0
      ) || 0;

      const prochaineFactureMontant = unpaidShipmentsTotal + unpaidBarrelSalesTotal;

      // Find earliest due date (estimate 30 days from creation for unpaid items)
      const allUnpaidDates = [
        ...unpaidShipments?.map(s => new Date(s?.created_at)),
        ...unpaidBarrelSales?.map(s => new Date(s?.created_at))
      ];
      
      const earliestUnpaidDate = allUnpaidDates?.length > 0 
        ? new Date(Math.min(...allUnpaidDates?.map(d => d?.getTime())))
        : new Date();
      
      const prochaineFactureDateEcheance = new Date(earliestUnpaidDate);
      prochaineFactureDateEcheance?.setDate(prochaineFactureDateEcheance?.getDate() + 30);

      // Économies réalisées (calculate discount savings)
      const totalDiscounts = shipments?.reduce((sum, s) => 
        sum + (parseFloat(s?.discount_amount) || 0), 0
      ) || 0;

      const economiesRealisees = totalDiscounts;

      // Get expeditions en cours with detailed tracking
      const expeditionsEnCoursDetails = shipments
        ?.filter(s => ['in_transit', 'customs', 'out_for_delivery']?.includes(s?.current_status))
        ?.map(shipment => {
          const events = shipment?.shipment_events || [];
          const sortedEvents = [...events]?.sort((a, b) => 
            new Date(a?.event_date) - new Date(b?.event_date)
          );

          // Build 5-step progress
          const etapes = [
            {
              label: 'Enlevé',
              date: sortedEvents?.find(e => e?.status === 'pending')?.event_date || null,
              completed: sortedEvents?.some(e => e?.status === 'pending')
            },
            {
              label: 'Réceptionné à l\'entrepôt',
              date: sortedEvents?.find(e => e?.location?.toLowerCase()?.includes('entrepôt'))?.event_date || null,
              completed: sortedEvents?.some(e => e?.location?.toLowerCase()?.includes('entrepôt'))
            },
            {
              label: 'Chargé dans le conteneur',
              date: shipment?.container?.departure_date || null,
              completed: !!shipment?.container_id
            },
            {
              label: 'En transit maritime',
              date: shipment?.current_status === 'in_transit' ? 'En cours' : null,
              completed: shipment?.current_status === 'in_transit',
              active: shipment?.current_status === 'in_transit'
            },
            {
              label: 'Arrivé à Abidjan',
              date: shipment?.actual_delivery_date || (shipment?.estimated_delivery_date ? `${shipment?.estimated_delivery_date} (estimé)` : 'À venir'),
              completed: shipment?.current_status === 'delivered'
            }
          ];

          return {
            reference: shipment?.reference,
            type: shipment?.shipment_type || 'Volume',
            dateDepart: shipment?.container?.departure_date || shipment?.created_at,
            navire: shipment?.container?.vessel_name || 'À confirmer',
            etapes
          };
        }) || [];

      // Get recent notifications from shipment events
      const recentEvents = shipments
        ?.flatMap(s => 
          (s?.shipment_events || [])?.map(e => ({
            id: e?.id,
            message: `Votre expédition ${s?.reference} - ${e?.description}`,
            date: e?.event_date,
            read: false,
            shipmentId: s?.id
          }))
        )
        ?.sort((a, b) => new Date(b?.date) - new Date(a?.date))
        ?.slice(0, 5) || [];

      // Add barrel deposit notifications
      const barrelNotifications = barrelDeposits
        ?.filter(d => d?.collection_status === 'scheduled' || d?.collection_status === 'in_progress')
        ?.slice(0, 3)
        ?.map(d => ({
          id: `barrel-${d?.id}`,
          message: `Livraison de ${d?.quantity} barrique(s) ${d?.barrel_type} prévue le ${d?.planned_collection_date}`,
          date: d?.updated_at,
          read: false,
          barrelDepositId: d?.id
        })) || [];

      const notifications = [...recentEvents, ...barrelNotifications]
        ?.sort((a, b) => new Date(b?.date) - new Date(a?.date))
        ?.slice(0, 5);

      // Get next departure info using the same service as public page
      let prochainDepart = null;
      
      try {
        const nextDepartureInfo = await nextDepartureService?.getActive();
        
        if (nextDepartureInfo?.departureDate) {
          // Parse the French date format
          const parsedDate = parseFrenchDate(nextDepartureInfo?.departureDate);

          if (parsedDate && !isNaN(parsedDate?.getTime())) {
            prochainDepart = {
              date: parsedDate?.toISOString(), // Convert to ISO format for consistent handling
              joursRestants: Math.floor(
                (parsedDate - new Date()) / (1000 * 60 * 60 * 24)
              ),
              vesselName: nextDepartureInfo?.vesselName,
              estimatedArrival: nextDepartureInfo?.estimatedArrival,
              port: nextDepartureInfo?.port
            };
          } else {
            console.warn('Could not parse departure date:', nextDepartureInfo?.departureDate);
          }
        }
      } catch (error) {
        console.error('Error fetching next departure:', error);
      }

      return {
        kpis: {
          expeditionsEnCours,
          totalExpedieCetteAnnee,
          prochaineFacture: {
            montant: Math.round(prochaineFactureMontant),
            dateEcheance: prochaineFactureDateEcheance?.toISOString()?.split('T')?.[0]
          },
          economiesRealisees: Math.round(economiesRealisees)
        },
        expeditionsEnCours: expeditionsEnCoursDetails,
        notifications,
        prochainDepart,
        shipments: shipments?.map(s => ({
          id: s?.id,
          reference: s?.reference,
          type: s?.shipment_type,
          dateDepart: s?.container?.departure_date || s?.created_at,
          status: s?.current_status,
          containerId: s?.container_id,
          containerCode: s?.container?.container_code,
          items: s?.shipment_items,
          events: s?.shipment_events,
          files: s?.shipment_files,
          paymentStatus: s?.payment_status,
          totalAmount: s?.shipment_items?.reduce((sum, item) => 
            sum + (parseFloat(item?.price_total) || 0), 0
          ) || 0
        })),
        barrelSales: barrelSales?.map(s => ({
          id: s?.id,
          barrelType: s?.barrel_type,
          quantity: s?.quantity,
          totalPrice: parseFloat(s?.total_price) || 0,
          paymentStatus: s?.payment_status,
          orderStatus: s?.order_status,
          orderDate: s?.order_date,
          deliveryDate: s?.delivery_date
        })),
        barrelDeposits: barrelDeposits?.map(d => ({
          id: d?.id,
          barrelType: d?.barrel_type,
          quantity: d?.quantity,
          totalPrice: parseFloat(d?.total_price) || 0,
          collectionStatus: d?.collection_status,
          plannedCollectionDate: d?.planned_collection_date,
          assignedDriver: d?.employees
        }))
      };
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time shipment updates for professional client
   * @param {string} clientEmail - Professional client email
   * @param {Function} callback - Callback function to handle updates
   * @returns {Object} Subscription object with unsubscribe method
   */
  subscribeToShipmentUpdates(clientEmail, callback) {
    if (!clientEmail || !callback) {
      console.error('Client email and callback are required for subscription');
      return { unsubscribe: () => {} };
    }

    // Get client_id for this email asynchronously, but return subscription immediately
    let shipmentsChannel = null;
    let eventsChannel = null;

    // Fetch client record and setup subscriptions
    supabase
      ?.from('clients')
      ?.select('id')
      ?.eq('email', clientEmail)
      ?.maybeSingle()?.then(({ data: clientRecord }) => {
        if (!clientRecord?.id) {
          console.warn('No client record found for email:', clientEmail);
          return;
        }

        // Subscribe to shipments table changes for this client_id
        shipmentsChannel = supabase
          ?.channel(`professional-shipments-${clientEmail}`)
          ?.on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'shipments',
              filter: `client_id=eq.${clientRecord?.id}`
            },
            (payload) => {
              console.log('Shipment update received:', payload);
              callback({ type: 'shipment', payload });
            }
          )
          ?.subscribe();

        // Subscribe to shipment_events table changes
        eventsChannel = supabase
          ?.channel(`professional-events-${clientEmail}`)
          ?.on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'shipment_events'
            },
            (payload) => {
              console.log('Shipment event update received:', payload);
              callback({ type: 'event', payload });
            }
          )
          ?.subscribe();
      })?.catch((error) => {
        console.error('Error subscribing to shipment updates:', error);
      });

    // Return unsubscribe function immediately
    return {
      unsubscribe: () => {
        if (shipmentsChannel) supabase?.removeChannel(shipmentsChannel);
        if (eventsChannel) supabase?.removeChannel(eventsChannel);
        console.log('Unsubscribed from real-time updates');
      }
    };
  },

  /**
   * Subscribe to real-time KPI updates (barrel sales, deposits, containers)
   * @param {string} clientEmail - Professional client email
   * @param {Function} callback - Callback function to handle updates
   * @returns {Object} Subscription object with unsubscribe method
   */
  subscribeToKPIUpdates(clientEmail, callback) {
    if (!clientEmail || !callback) {
      console.error('Client email and callback are required for KPI subscription');
      return { unsubscribe: () => {} };
    }

    // Subscribe to barrel_sales changes
    const barrelSalesChannel = supabase
      ?.channel(`professional-barrel-sales-${clientEmail}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'barrel_sales',
          filter: `customer_email=eq.${clientEmail}`
        },
        (payload) => {
          console.log('Barrel sales update received:', payload);
          callback({ type: 'barrel_sales', payload });
        }
      )
      ?.subscribe();

    // Subscribe to barrel_deposits changes
    const barrelDepositsChannel = supabase
      ?.channel(`professional-barrel-deposits-${clientEmail}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'barrel_deposits'
        },
        (payload) => {
          console.log('Barrel deposits update received:', payload);
          callback({ type: 'barrel_deposits', payload });
        }
      )
      ?.subscribe();

    // Subscribe to containers changes (for shipments tracking)
    const containersChannel = supabase
      ?.channel(`professional-containers-${clientEmail}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'containers'
        },
        (payload) => {
          console.log('Container update received:', payload);
          callback({ type: 'container', payload });
        }
      )
      ?.subscribe();

    // Return unsubscribe function
    return {
      unsubscribe: () => {
        supabase?.removeChannel(barrelSalesChannel);
        supabase?.removeChannel(barrelDepositsChannel);
        supabase?.removeChannel(containersChannel);
        console.log('Unsubscribed from KPI updates');
      }
    };
  },

  /**
   * Get all shipments for professional client
   * @returns {Promise<Array>} List of shipments with full details
   */
  async getShipments() {
    try {
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();

      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      const { data: professionalClient, error: clientError } = await supabase
        ?.from('professional_clients')
        ?.select('email')
        ?.eq('auth_user_id', user?.id)
        ?.single();

      if (clientError) throw clientError;

      const { data: shipments, error: shipmentsError } = await supabase
        ?.from('shipments')
        ?.select(`
          *,
          container:container_id (
            container_code,
            status,
            departure_date,
            eta_date,
            vessel_name
          ),
          shipment_items (
            quantity,
            price_total,
            catalog_item:catalog_item_id (name)
          ),
          shipment_events (
            status,
            location,
            description,
            event_date
          ),
          shipment_files (
            file_type,
            file_url,
            file_name
          )
        `)
        ?.order('created_at', { ascending: false });

      if (shipmentsError) throw shipmentsError;

      return shipments || [];
    } catch (error) {
      console.error('Error fetching shipments:', error);
      throw error;
    }
  },

  /**
   * Get shipment details by ID
   * @param {string} shipmentId - Shipment ID
   * @returns {Promise<Object>} Shipment details with photos and documents
   */
  async getShipmentDetails(shipmentId) {
    try {
      const { data: shipment, error } = await supabase
        ?.from('shipments')
        ?.select(`
          *,
          container:container_id (*),
          shipment_items (
            *,
            catalog_item:catalog_item_id (*)
          ),
          shipment_events (*),
          shipment_files (*)
        `)
        ?.eq('id', shipmentId)
        ?.maybeSingle();

      if (error) throw error;
      if (!shipment) {
        throw new Error('Expédition introuvable');
      }

      // Get signed URLs for photos
      const photosUrls = shipment?.photos_urls || [];
      const signedPhotos = await Promise.all(
        photosUrls?.map(async (url) => {
          try {
            const path = url?.split('/')?.pop();
            const { data: signedData } = await supabase?.storage
              ?.from('shipment-photos')
              ?.createSignedUrl(path, 3600);
            return signedData?.signedUrl || url;
          } catch (err) {
            return url;
          }
        })
      );

      return {
        ...shipment,
        signedPhotos
      };
    } catch (error) {
      console.error('Error fetching shipment details:', error);
      throw error;
    }
  },

  /**
   * Get all documents (invoices, certificates, etc.)
   * @returns {Promise<Object>} Categorized documents
   */
  async getDocuments() {
    try {
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();

      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      const { data: professionalClient, error: clientError } = await supabase
        ?.from('professional_clients')
        ?.select('email')
        ?.eq('auth_user_id', user?.id)
        ?.single();

      if (clientError) throw clientError;

      // Get the client record linked to this professional client's email
      const { data: clientRecord, error: clientRecordError } = await supabase
        ?.from('clients')
        ?.select('id')
        ?.eq('email', professionalClient?.email)
        ?.maybeSingle();

      if (clientRecordError) throw clientRecordError;

      // If no linked client record, return empty documents
      if (!clientRecord?.id) {
        return { factures: [], certificats: [], photos: [], autresDocuments: [] };
      }

      // Get shipments filtered by this client's ID only
      const { data: shipments, error: shipmentsError } = await supabase
        ?.from('shipments')
        ?.select(`
          id,
          reference,
          created_at,
          invoice_url,
          document_url,
          extra_documents_urls,
          photos_urls,
          shipment_files (*)
        `)
        ?.eq('client_id', clientRecord?.id)
        ?.order('created_at', { ascending: false });

      if (shipmentsError) throw shipmentsError;

      // Categorize documents
      const factures = [];
      const certificats = [];
      const photos = [];
      const autresDocuments = [];

      shipments?.forEach(shipment => {
        // Invoices
        if (shipment?.invoice_url) {
          factures?.push({
            id: `invoice-${shipment?.id}`,
            numero: `F-${shipment?.reference}`,
            dateEmission: shipment?.created_at,
            expeditionReference: shipment?.reference,
            url: shipment?.invoice_url,
            statut: 'disponible'
          });
        }

        // Files from shipment_files
        shipment?.shipment_files?.forEach(file => {
          if (file?.file_type === 'invoice') {
            factures?.push({
              id: file?.id,
              numero: file?.file_name,
              dateEmission: shipment?.created_at,
              expeditionReference: shipment?.reference,
              url: file?.file_url,
              statut: 'disponible'
            });
          } else if (file?.file_type === 'douane') {
            certificats?.push({
              id: file?.id,
              numero: file?.file_name,
              date: shipment?.created_at,
              expeditionReference: shipment?.reference,
              url: file?.file_url,
              statut: 'visé'
            });
          } else {
            autresDocuments?.push({
              id: file?.id,
              nom: file?.file_name,
              type: file?.file_type,
              date: shipment?.created_at,
              expeditionReference: shipment?.reference,
              url: file?.file_url
            });
          }
        });

        // Photos
        const photosArray = shipment?.photos_urls || [];
        photosArray?.forEach((photoUrl, index) => {
          photos?.push({
            id: `photo-${shipment?.id}-${index}`,
            expeditionReference: shipment?.reference,
            url: photoUrl,
            date: shipment?.created_at
          });
        });
      });

      return {
        factures,
        certificats,
        photos,
        autresDocuments
      };
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  },

  /**
   * Get cost analytics data
   * @returns {Promise<Object>} Cost breakdown and analytics
   */
  async getCostAnalytics() {
    try {
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();

      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      const { data: professionalClient, error: clientError } = await supabase
        ?.from('professional_clients')
        ?.select('email')
        ?.eq('auth_user_id', user?.id)
        ?.single();

      if (clientError) throw clientError;

      // Get all shipments
      const { data: shipments, error: shipmentsError } = await supabase
        ?.from('shipments')
        ?.select(`
          *,
          shipment_items (
            price_total,
            catalog_item:catalog_item_id (category)
          )
        `)
        ?.order('created_at', { ascending: false });

      if (shipmentsError) throw shipmentsError;

      // Get barrel sales
      const { data: barrelSales, error: salesError } = await supabase
        ?.from('barrel_sales')
        ?.select('*')
        ?.eq('customer_email', professionalClient?.email)
        ?.order('created_at', { ascending: false });

      if (salesError) throw salesError;

      const now = new Date();
      const startOfYear = new Date(now.getFullYear(), 0, 1);

      // Calculate KPIs
      const shipmentsThisYear = shipments?.filter(s => new Date(s?.created_at) >= startOfYear) || [];
      const barrelSalesThisYear = barrelSales?.filter(s => new Date(s?.created_at) >= startOfYear) || [];

      const depensesTotales = [
        ...shipmentsThisYear?.map(s => 
          s?.shipment_items?.reduce((sum, item) => sum + (parseFloat(item?.price_total) || 0), 0) || 0
        ),
        ...barrelSalesThisYear?.map(s => parseFloat(s?.total_price) || 0)
      ]?.reduce((sum, val) => sum + val, 0);

      const totalExpeditions = shipmentsThisYear?.length + barrelSalesThisYear?.length;
      const coutMoyen = totalExpeditions > 0 ? depensesTotales / totalExpeditions : 0;

      const volumeTotal = shipmentsThisYear?.length;
      const economiesRealisees = shipments?.reduce((sum, s) => 
        sum + (parseFloat(s?.discount_amount) || 0), 0
      ) || 0;

      // Monthly cost evolution
      const monthlyData = {};
      [...shipmentsThisYear, ...barrelSalesThisYear]?.forEach(item => {
        const month = new Date(item?.created_at)?.toISOString()?.slice(0, 7);
        if (!monthlyData?.[month]) {
          monthlyData[month] = { coutReel: 0, coutStandard: 0 };
        }
        const amount = item?.shipment_items 
          ? item?.shipment_items?.reduce((sum, i) => sum + (parseFloat(i?.price_total) || 0), 0)
          : parseFloat(item?.total_price) || 0;
        monthlyData[month].coutReel += amount;
        monthlyData[month].coutStandard += amount * 1.15; // Assume 15% standard markup
      });

      const evolutionCouts = Object.entries(monthlyData)?.map(([month, data]) => ({
        mois: month,
        coutReel: Math.round(data?.coutReel),
        coutStandard: Math.round(data?.coutStandard)
      }));

      // Type breakdown
      const typeBreakdown = {};
      shipmentsThisYear?.forEach(s => {
        const type = s?.shipment_type || 'Autre';
        const amount = s?.shipment_items?.reduce((sum, item) => 
          sum + (parseFloat(item?.price_total) || 0), 0
        ) || 0;
        typeBreakdown[type] = (typeBreakdown?.[type] || 0) + amount;
      });

      barrelSalesThisYear?.forEach(s => {
        const type = 'Barriques';
        typeBreakdown[type] = (typeBreakdown?.[type] || 0) + (parseFloat(s?.total_price) || 0);
      });

      const repartitionParType = Object.entries(typeBreakdown)?.map(([type, montant]) => ({
        type,
        montant: Math.round(montant),
        pourcentage: Math.round((montant / depensesTotales) * 100)
      }));

      return {
        kpis: {
          depensesTotales: Math.round(depensesTotales),
          coutMoyen: Math.round(coutMoyen),
          volumeTotal,
          economiesRealisees: Math.round(economiesRealisees)
        },
        evolutionCouts,
        repartitionParType
      };
    } catch (error) {
      console.error('Error fetching cost analytics:', error);
      throw error;
    }
  }
};

export default professionalClientDashboardService;